/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable import/no-cycle */
/* eslint-disable no-nested-ternary */
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { addOneReportMedia } from '@brands/services/oneReport/addOneReportMedia';
import { confirmOneReportMedia } from '@brands/services/oneReport/confirmOneReportMedia';
import { registerOneReportMedia } from '@brands/services/oneReport/registerOneReportMedia';
import { base64ListToFileList } from '@brands/Utils/base64ToFile';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { getFileMimeType } from '@brands/Utils/truncateString';
import { parsePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';
import { MultiValue } from 'react-select';
import { InsuranceForm } from 'src/brands/dentistry-one/Dashboard/Insurance/utils/types';
import { UserProfile } from 'src/brands/dentistry-one/services/identity/types/UserProfile';

import { PaymentProfile } from '../../../../services/identity/types/PaymentProfile';
import {
  Address,
  BasicInfo,
  Insurance,
  Marker,
  Organization,
  UserRoleName,
} from '../../../../services/identity/types/UserProfile';
import { Option } from '../../../../Utils/selectOptions';
import { PatientFormFiveSubmit, PatientNotCoveredInsuranceSubmit } from '../../../PatientForms/PatientForm/utils/types';
import { DependentSubmit } from '../Sections/Dependent/EditDependent';
import { MyProfileSubmit, TokenResult } from './types';

export interface UploadedFile {
  name: string;
  dataURL?: string;
  media_id?: string;
  mimetype?: string;
}

export const updateData = (data: BasicInfo): BasicInfo => {
  const updatedData = { ...data };

  if (data.date_of_birth) {
    updatedData.date_of_birth = moment(data.date_of_birth).format('YYYY-MM-DD');
  }

  return updatedData;
};

const THREE_MINUTES_IN_MS = 0.5 * 60 * 1000; // 0.5 minutes in milliseconds

export const isAlmostEqualWithTolerance = (date1: string, date2: string): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  const differenceInMs = Math.abs(d1.getTime() - d2.getTime());
  return differenceInMs <= THREE_MINUTES_IN_MS;
};

export const uploadMediaToCase = async (
  newCase: ICase,
  imagesValue: string | number | string[] | UploadedFile[] | undefined | boolean | Insurance,
  userRole?: string,
): Promise<void> => {
  if (!Array.isArray(imagesValue)) return;

  // Some staff-scoped POST /cases responses (notably Care Advisor) return the case
  // without the embedded `one_report` resource that the admin endpoint returns.
  // Re-fetch the case so we can resolve the `one_report_id` before attaching media.
  let oneReportId = newCase?.one_report ? newCase.one_report.id : newCase?.one_report_id?.toString() || '';
  if (!oneReportId && newCase?.id) {
    try {
      const refreshedCase = await getOneCase(
        Number(newCase.id),
        {
          include_patient_care_advisor_conversation: false,
          include_patient_provider_conversation: false,
          include_provider_care_advisor_conversation: false,
        },
        userRole,
      );
      oneReportId = refreshedCase?.one_report?.id || refreshedCase?.one_report_id?.toString() || '';
    } catch (error: unknown) {
      displayErrorDetails(error);
      return;
    }
  }
  if (!oneReportId) return;

  await Promise.all(
    imagesValue.map(async (raw) => {
      if (typeof raw === 'string') return;
      const image = raw as UploadedFile;
      try {
        // Fast path: image was already uploaded to S3 at capture time.
        if (image.media_id) {
          await addOneReportMedia(oneReportId, { media_id: image.media_id, note: '' }, userRole);
          return;
        }
        // Legacy path: dataURL only — upload now and attach. Mirror the role-scoped
        // routing used by `uploadIntakeImage` so Care Advisor / Collector callers hit
        // `/care_advisor/media/...` and `/collector/media/...` instead of the
        // unscoped admin route.
        if (image.dataURL) {
          const file = base64ListToFileList(image.dataURL) as File | null;
          if (!file) return;
          const mimeType = getFileMimeType(image.name);
          const payload: RegisterMediaPayload = {
            files: [{ filename: image.name, mimetype: mimeType }],
          };
          const registeredMedia = await registerOneReportMedia(payload, userRole);
          const mediaId = registeredMedia.files[0]?.upload_id;
          const uploadUrl = registeredMedia.files[0]?.upload_url;
          if (!mediaId || !uploadUrl) return;
          await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'content-type': mimeType } });
          await confirmOneReportMedia(mediaId, userRole);

          await addOneReportMedia(oneReportId, { media_id: mediaId, note: '' }, userRole);
        }
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    }),
  );
};

export function convertDateFormat(inputDate: string): string {
  const date = new Date(inputDate);
  const isoDate = date?.toISOString();
  return isoDate;
}

export const areArraysEqual = (arr1: Organization[] | Marker[], arr2: MultiValue<Option>): boolean => {
  // Check the lengths
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Create a set of arr1 ids
  const idSet = new Set(arr1.map((org) => org.id));

  // Check if all arr2.id values exist in arr1
  for (const option of arr2) {
    if (!idSet.has(option.id as string)) {
      return false;
    }
  }

  return true;
};

export const updateDataInsurance = (data: MyProfileSubmit | DependentSubmit | PatientFormFiveSubmit): Insurance => {
  switch (data.insurance_through) {
    case 'none':
      return {
        insured_by: data.insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          insurance_carrier_id: data.insurance_carrier_id,
          phone_number: parsePhoneNumber(data.iPhoneNumber, 'US')?.format('E.164') as string,
          group_number: data.group_number,
          address1: (data as PatientFormFiveSubmit).insurance_address || '',
          city: (data as PatientFormFiveSubmit).insurance_city || '',
          state: (data as PatientFormFiveSubmit).insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          phone_number: parsePhoneNumber(data.iPhoneNumber, 'US')?.format('E.164') as string,
          insurance_carrier_id: data.insurance_carrier_id,
          group_number: data.group_number,
          address1: (data as PatientFormFiveSubmit).insurance_address || '',
          city: (data as PatientFormFiveSubmit).insurance_city || '',
          state: (data as PatientFormFiveSubmit).insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
        insurance_through: {
          first_name: data.iFirstName,
          middle_name: data.iMiddleName,
          date_of_birth: data.iDateOfBirth ? moment(data.iDateOfBirth).format('YYYY-MM-DD') : '0000-00-00',
          gender_identity: data.iGenderIdentity,
          last_name: data.iLastName,
          address: {
            address1: data.iAddress1,
            address2: data.iAddress2,
            city: data.iCity,
            state: data.iState,
            zip_code: data.iZipCode,
            country: data.country || 'United States of America',
          },
        },
      };
  }
};

export const updateDataInsuranceNewDesign = (data: InsuranceForm | PatientFormFiveSubmit): Insurance => {
  switch (data.insurance_through) {
    case 'none':
      return {
        insured_by: data.insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          insurance_carrier_id: data.insurance_carrier_id,
          phone_number: data.insurance_phone_number
            ? (parsePhoneNumber(data.insurance_phone_number, 'US')?.format('E.164') as string)
            : '',
          group_number: data.group_number,
          address1: data.insurance_address || '',
          city: data.insurance_city || '',
          state: data.insurance_state || '',
          zip_code: data.insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.insurance_through,
        insurance_membership: {
          member_id: data.member_id,
          phone_number: data?.insurance_phone_number
            ? (parsePhoneNumber(data.insurance_phone_number, 'US')?.format('E.164') as string)
            : '',
          insurance_carrier_id: data.insurance_carrier_id,
          group_number: data.group_number,
          address1: data.insurance_address || '',
          city: data.insurance_city || '',
          state: data.insurance_state || '',
          zip_code: data.insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
        insurance_through: {
          first_name: 'insurance_first_name' in data ? data.insurance_first_name : data.iFirstName,
          date_of_birth:
            'insurance_date_of_birth' in data
              ? data.insurance_date_of_birth
                ? moment(data.insurance_date_of_birth).format('YYYY-MM-DD')
                : '0000-00-00'
              : data.iDateOfBirth
                ? moment(data.iDateOfBirth).format('YYYY-MM-DD')
                : '0000-00-00',
          gender_identity: 'insurance_gender' in data ? data.insurance_gender : data.iGenderIdentity,
          last_name: 'insurance_last_name' in data ? data.insurance_last_name : data.iLastName,
          address:
            'insurance_address1' in data
              ? {
                  address1: data.insurance_address1,
                  address2: data.insurance_address2,
                  city: data.insurance_city1,
                  state: data.insurance_state1,
                  zip_code: data.insurance_zip_code1,
                }
              : {
                  address1: data.iAddress1,
                  address2: data.iAddress2,
                  city: data.iCity,
                  state: data.iState,
                  zip_code: data.iZipCode,
                },
        },
      };
  }
};

export const convertInsuranceToInsuranceForm = (insurance: Insurance, user: UserProfile): InsuranceForm => {
  const phone = parsePhoneNumberFromString(insurance.insurance_membership?.phone_number || '', 'US');
  const insuranceThrough = insurance.insurance_through;

  return {
    member_id: insurance.insurance_membership?.member_id || '',
    group_number: insurance.insurance_membership?.group_number || '',
    insurance_through: insurance.insured_by || '',
    insurance_carrier_id: insurance.insurance_membership?.insurance_carrier_id,
    insurance_phone_number: phone?.formatNational() || '',

    insurance_address: insurance.insurance_membership?.address1 || '',
    insurance_city: insurance.insurance_membership?.city || '',
    insurance_state: insurance.insurance_membership?.state || '',
    insurance_zip_code: insurance.insurance_membership?.zip_code || '',

    insurance_first_name: insuranceThrough?.first_name || user.basic_info.first_name || '',
    insurance_date_of_birth: insuranceThrough?.date_of_birth || user.basic_info.date_of_birth || '',
    insurance_gender: insuranceThrough?.gender_identity || user.basic_info.gender || '',
    insurance_last_name: insuranceThrough?.last_name || user.basic_info.last_name || '',

    insurance_address1: insuranceThrough?.address?.address1 || '',
    insurance_address2: insuranceThrough?.address?.address2 || '',
    insurance_zip_code1: insuranceThrough?.address?.zip_code || '',
    insurance_state1: insuranceThrough?.address?.state || '',
    insurance_city1: insuranceThrough?.address?.city || '',

    front_of_card: [] as UploadedFile[],
    back_of_card: [] as UploadedFile[],

    media: insurance.media || [],
    state_funded_coverage_status: insurance.state_funded_coverage_status || '',
  } as InsuranceForm;
};

export const updateDataSecondaryInsurance = (data: MyProfileSubmit | DependentSubmit): Insurance => {
  switch (data.secondary_insurance_through) {
    case 'none':
      return {
        insured_by: data.secondary_insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          phone_number: parsePhoneNumber(data.secondary_iPhoneNumber, 'US')?.format('E.164') as string,
          group_number: data.secondary_group_number,
          address1: (data as PatientFormFiveSubmit).secondary_insurance_address || '',
          city: (data as PatientFormFiveSubmit).secondary_insurance_city || '',
          state: (data as PatientFormFiveSubmit).secondary_insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          phone_number: parsePhoneNumber(data.secondary_iPhoneNumber, 'US')?.format('E.164') as string,
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          group_number: data.secondary_group_number,
          address1: (data as PatientFormFiveSubmit).secondary_insurance_address || '',
          city: (data as PatientFormFiveSubmit).secondary_insurance_city || '',
          state: (data as PatientFormFiveSubmit).secondary_insurance_state || '',
          zip_code: (data as PatientFormFiveSubmit).secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: (data as PatientFormFiveSubmit).state_funded_coverage_status || '',
        insurance_through: {
          first_name: data.secondary_iFirstName,
          middle_name: data.secondary_iMiddleName,
          date_of_birth: data.secondary_iDateOfBirth
            ? moment(data.secondary_iDateOfBirth).format('YYYY-MM-DD')
            : '0000-00-00',
          gender_identity: data.secondary_iGenderIdentity,
          last_name: data.secondary_iLastName,
          address: {
            address1: data.secondary_iAddress1,
            address2: data.secondary_iAddress2,
            city: data.secondary_iCity,
            state: data.secondary_iState,
            zip_code: data.secondary_iZipCode,
          },
        },
      };
  }
};

export const updateDataSecondaryInsuranceNewDesign = (data: InsuranceForm | PatientFormFiveSubmit): Insurance => {
  switch (data.secondary_insurance_through) {
    case 'none':
      return {
        insured_by: data.secondary_insurance_through,
      };
    case 'myself':
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          phone_number: data.secondary_insurance_phone_number
            ? (parsePhoneNumber(data.secondary_insurance_phone_number, 'US')?.format('E.164') as string)
            : '',
          group_number: data.secondary_group_number,
          address1: data.secondary_insurance_address || '',
          city: data.secondary_insurance_city || '',
          state: data.secondary_insurance_state || '',
          zip_code: data.secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
      };
    case '':
      return {
        insured_by: '',
        insurance_membership: undefined,
        insurance_through: undefined,
      };
    default:
      return {
        insured_by: data.secondary_insurance_through,
        insurance_membership: {
          member_id: data.secondary_member_id,
          phone_number: data.secondary_insurance_phone_number
            ? (parsePhoneNumber(data.secondary_insurance_phone_number, 'US')?.format('E.164') as string)
            : '',
          insurance_carrier_id: data.secondary_insurance_carrier_id,
          group_number: data.secondary_group_number,
          address1: data.secondary_insurance_address || '',
          city: data.secondary_insurance_city || '',
          state: data.secondary_insurance_state || '',
          zip_code: data.secondary_insurance_zip_code || '',
        },
        state_funded_coverage_status: data.state_funded_coverage_status || '',
        insurance_through: {
          first_name:
            'secondary_insurance_first_name' in data ? data.secondary_insurance_first_name : data.secondary_iFirstName,
          date_of_birth:
            'secondary_insurance_date_of_birth' in data
              ? data.secondary_insurance_date_of_birth
                ? moment(data.secondary_insurance_date_of_birth).format('YYYY-MM-DD')
                : '0000-00-00'
              : data.secondary_iDateOfBirth
                ? moment(data.secondary_iDateOfBirth).format('YYYY-MM-DD')
                : '0000-00-00',
          gender_identity:
            'secondary_insurance_gender' in data ? data.secondary_insurance_gender : data.secondary_iGenderIdentity,
          last_name:
            'secondary_insurance_last_name' in data ? data.secondary_insurance_last_name : data.secondary_iLastName,
          address:
            'secondary_insurance_address1' in data
              ? {
                  address1: data.secondary_insurance_address1,
                  address2: data.secondary_insurance_address2,
                  city: data.secondary_insurance_city1,
                  state: data.secondary_insurance_state1,
                  zip_code: data.secondary_insurance_zip_code1,
                }
              : {
                  address1: data.secondary_iAddress1,
                  address2: data.secondary_iAddress2,
                  city: data.secondary_iCity,
                  state: data.secondary_iState,
                  zip_code: data.secondary_iZipCode,
                },
        },
      };
  }
};

export const updateDataPayment = (
  data: PatientFormFiveSubmit | PatientNotCoveredInsuranceSubmit,
  tokenResult: TokenResult,
  userInfo: any,
  isSameAddress: boolean,
  dependentAddress: Address,
  selectedPatientId: number,
  patientId?: number,
): PaymentProfile => {
  return {
    ...(userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin ||
    userInfo.role.name === UserRoleName.Collector ||
    userInfo.role.name === UserRoleName.CareAdvisor
      ? { user_id: patientId }
      : {}),
    verification_token: tokenResult.token,
    type: 'Card',
    is_default: true,
    credit_card: {
      brand: tokenResult.details?.card.brand,
      exp_month: tokenResult.details?.card.expMonth,
      exp_year: tokenResult.details?.card.expYear,
      last_four: tokenResult.details?.card.last4,
    },
    billing_contact: {
      address1: isSameAddress
        ? data.creditCardAddress1
        : userInfo.id === selectedPatientId
          ? userInfo.address?.address1
          : dependentAddress?.address1,
      address2: isSameAddress
        ? data.creditCardAddress2
        : userInfo.id === selectedPatientId
          ? userInfo.address?.address2
          : dependentAddress?.address2,
      city: isSameAddress
        ? data.creditCardCity
        : userInfo.id === selectedPatientId
          ? userInfo.address?.city
          : dependentAddress?.city,
      state: isSameAddress
        ? data.creditCardState
        : userInfo.id === selectedPatientId
          ? userInfo.address?.state
          : dependentAddress?.state,
      postal_code: isSameAddress
        ? data.creditCardZipCode
        : userInfo.id === selectedPatientId
          ? userInfo.address?.zip_code
          : dependentAddress?.zip_code,
      family_name: '',
      given_name: '',
      email: userInfo.email,
      phone: userInfo.phone_number,
    },
  };
};
